import { useEffect, useState } from "react";
import { SettingsAPI, VisitNoteEditPolicySettings } from "../services/settings.service";

// The Visit Notes edit-rights policy is configured in clinic settings and
// enforced by the backend on every PUT /notes/{id}. This hook is read-only
// UI convenience — it lets the notes panel compute a same-render `can_edit`
// hint without waiting on a second round trip; the server re-checks on every
// write regardless (see app/modules/doctor_note/policy.py).

const CACHE_KEY = "kgz_visit_note_edit_policy_cache";

// Fail-safe default: until the real policy is known, assume the stricter
// primary_only (the server default) so the UI never over-offers an edit
// affordance a slow/failed fetch would have hidden.
const DEFAULT_POLICY: VisitNoteEditPolicySettings = {
  visit_note_edit_policy: "primary_only",
};

function readCache(): VisitNoteEditPolicySettings | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as VisitNoteEditPolicySettings) : null;
  } catch {
    return null;
  }
}

/** Persist the latest policy so the notes panel reflects it immediately after a save. */
export function cacheVisitNoteEditPolicy(data: VisitNoteEditPolicySettings): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

/**
 * Read the clinic's Visit Notes edit-rights policy.
 * Seeds synchronously from the cache, then refreshes from the API. Falls back
 * to the cache / strict default when the network is unavailable.
 */
export function useVisitNoteEditPolicy(): VisitNoteEditPolicySettings {
  const [policy, setPolicy] = useState<VisitNoteEditPolicySettings>(
    () => readCache() ?? DEFAULT_POLICY
  );

  useEffect(() => {
    let cancelled = false;
    SettingsAPI.getVisitNoteEditPolicy()
      .then((data) => {
        if (cancelled) return;
        setPolicy(data);
        cacheVisitNoteEditPolicy(data);
      })
      .catch(() => { /* keep cached / default */ });
    return () => { cancelled = true; };
  }, []);

  return policy;
}
